'use client'

import { Modal } from '@/Modal'
import { CheckIcon } from '@heroicons/react/24/outline'

export const EmailSuggestionModal = ({
  isOpen,
  onClose,
  suggestion,
  originalEmail,
  correctedEmail,
  onAcceptSuggestion,
  onDecline,
}: EmailSuggestionModalProps) => {
  // Extract the suggested email from the suggestion text if correctedEmail not provided
  const extractSuggestedEmail = (suggestionText: string) => {
    const match = suggestionText.match(/([^:\s]+@[^?\s]+)/)
    return match ? match[1] : ''
  }

  const suggestedEmail =
    correctedEmail || (suggestion ? extractSuggestedEmail(suggestion) : '')

  const handleAccept = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    e.nativeEvent.stopImmediatePropagation()
    if (suggestedEmail && suggestedEmail.trim()) {
      try {
        onAcceptSuggestion(suggestedEmail)
      } catch (error) {
        console.error('Error in handleAccept:', error)
      }
    }
  }

  const handleDecline = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    onDecline(originalEmail)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="md"
      showCloseButton={true}
      closeOnBackdropClick={false}
    >
      <div className="relative">
        {/* Top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500 rounded-t-2xl" />

        {/* Header */}
        <div className="relative bg-gradient-to-br from-amber-50 to-orange-50 px-6 pt-8 pb-6 overflow-hidden">
          <div className="flex flex-col items-center">
            <h3 className="text-xl font-bold text-gray-900 mb-1">
              Email Suggestion
            </h3>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          <div className="text-gray-600 text-center mb-6">
            <p className="mb-4">
              We detected a possible typo in your email address:
            </p>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-500 mb-2">You entered:</p>
              <p className="font-mono text-gray-700">{originalEmail}</p>
            </div>

            <div className="bg-amber-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-600 mb-2">Did you mean:</p>
              <p className="font-mono text-amber-800 font-medium">
                {suggestedEmail}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <button
              type="button"
              onClick={handleAccept}
              className="w-full group relative overflow-hidden bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] cursor-pointer z-10"
              style={{ position: 'relative', zIndex: 10 }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              <div className="relative flex items-center justify-center z-10">
                <CheckIcon className="w-5 h-5 mr-2" />
                Use suggested email
              </div>
            </button>

            <button
              type="button"
              onClick={handleDecline}
              className="w-full group bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-xl transition-all duration-200 hover:shadow-md cursor-pointer"
            >
              Keep my original email
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

interface EmailSuggestionModalProps {
  isOpen: boolean
  onClose: () => void
  suggestion: string
  originalEmail: string
  correctedEmail?: string
  onAcceptSuggestion: (suggestedEmail: string) => void
  onDecline: (originalEmail: string) => void
}
